import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Column } from 'react-data-grid';
import { CustomizedDataGrid } from '../customized_data_grid';
import { Grid, Box, Chip, TextField, FormControl, Button, Select, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import { ReactComponent as DatePickerIcon } from '../../assets/calendar-outline_white.svg';
import { textEditor } from 'react-data-grid';

import { OverflowTooltip } from '../overflow_tooltip';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment from 'moment-timezone';

import '../jobExecutions/jobExecutions.css';
import '../jobs/jobs.css';
import Loader from '../Loader/loader';

import { CellExpanderFormatter } from '../cell_expander_formatter';

import {
	getJobExecutions,
	// getJobExecutionsMetrices,
	jobSearchForExecutions,
	updateJobRunHistory,
	getBoxRunsList,
	getActionTakenData,
	getReasons
} from '../../service/service';

import convertSeconds from '../../utils/convertSeconds';
import { useTheme } from "@mui/material/styles";

interface Row {
	runStatus: string;
	commonStatus: string;
	reasonForDelay: string;
	actionTaken: string;
	ticketNumber: string;
	userStatus: string;
	downstream: string;
	slaStatus: string;
	_id: string;
	name: string;
	jobId: string;
	status: string;
	exitCode: string;
	runNum: string;
	application: string;
	subApplication: string;
	runStartTime: string;
	runEndTime: string;
	slaEndTime: string;
	criticalFlag: boolean;
	machine: string;
	createdAt: string;
	updatedAt: string;
	updatedBy: string;
	boxName: string;
	jobType: string;
	children?: Row[];
	ischildren?: boolean;
	isExpanded?: boolean;
	actualDuration: string;
	overRun: boolean;
	thresholdPercent: string;
	durationDelay: string;
	avgRunTime: number;
	estimatedEndTime: string;
}

function getColumns(
	toggleSubRow: (name: string, runStartTime: string, runEndTime: any) => void,
	renderStatus: (status: any) => JSX.Element,
	renderThreshold: (threshold: any) => JSX.Element,
	reasonList: any[]
): Array<Column<Row>> {
	return [
		{
			key: 'name',
			name: 'Scheduled Job',
			width: 308,
			minWidth: 150,
			formatter({ row, isCellSelected }) {
				const hasChildren = row.jobType === 'BOX';
				return (
					<>
						{hasChildren && (
							<CellExpanderFormatter
								isCellSelected={isCellSelected}
								expanded={row.isExpanded === true}
								onCellExpand={() => {
									toggleSubRow(row.name, row.runStartTime, row.runEndTime);
								}}
							/>
						)}
						{'   '}
						<Link
							className="jobsNameList"
							to={`/jobs/${row.jobId}`}
							state={row}
							style={{ paddingLeft: hasChildren ? '5px' : '20px' }}
						>
							{row.name}
						</Link>
					</>
				);
			}
		},
		{
			key: 'boxName',
			name: 'Box Name',
			width: 200,
			minWidth: 150
		},
		{
			key: 'sector',
			name: 'Sector',
			width: 100,
			minWidth: 100
		},
		{
			key: 'system',
			name: 'System',
			width: 100,
			minWidth: 100
		},
		{
			key: 'process',
			name: 'Process',
			width: 120,
			minWidth: 120
		},
		{
			key: 'runStatus',
			name: 'Run Status',
			width: 155,
			minWidth: 155,
			cellClass: 'center-text',
			formatter(props) {
				const status = props.row.runStatus;
				return renderStatus(status);
			}
		},
		{
			key: 'runStartTime',
			name: 'Start Time',
			width: 140,
			minWidth: 140,
			formatter(props) {
				const startDate = props.row.runStartTime;
				if (startDate) {
					return moment(startDate).format('MM-DD-YY HH:mm:ss');
				}
				return '';
			}
		},
		{
			key: 'runEndTime',
			name: 'End Time',
			width: 140,
			minWidth: 140,
			formatter(props) {
				const endDate = props.row.runEndTime;
				if (endDate) {
					return moment(endDate).format('MM-DD-YY HH:mm:ss');
				}
				return '';
			}
		},
		{
			key: 'slaStatus',
			name: 'SLA Status',
			width: 120,
			minWidth: 120,
			cellClass: 'center-text',
			formatter(props) {
				return ['Met', 'Not Met'].includes(props.row.slaStatus) ? (
					<Chip
						label={props.row.slaStatus}
						color={props.row.slaStatus === 'Met' ? 'success' : 'secondary'}
						className="slaChip"
					/>
				) : (
					props.row.slaStatus
				);
			}
		},
		{
			key: 'slaEndTime',
			name: 'Normal Completion Time',
			minWidth: 200,
			width: 200,
			formatter(props) {
				return (
					<OverflowTooltip customTooltip={`Timezone: ${moment().isDST() ? 'EDT(UTC-4)' : 'EST(UTC-5)'}`}>
						{props.row.slaEndTime}
					</OverflowTooltip>
				);
			}
		},
		{
			key: 'estimatedEndTime',
			name: 'Estimated End Time',
			formatter(props) {
				return props.row.estimatedEndTime ? moment(props.row.estimatedEndTime).format() : '';
			},
			minWidth: 180
		},
		{
			key: 'criticalFlag',
			name: 'Critical',
			formatter(props) {
				return props.row.criticalFlag ? 'Yes' : 'No';
			},
			minWidth: 105
		},
		{
			key: 'reasonForDelay',
			name: 'Reason',
			width: 120,
			minWidth: 120,
			cellClass: 'has-editor',
			formatter(props) {
				if (props.row.reasonForDelay) {
					return props.row.reasonForDelay.length > 15 ? (
						<p className="reasonForDelay">{props.row.reasonForDelay}</p>
					) : (
						<p>{props.row.reasonForDelay}</p>
					);
				}
			},
			editor: (p) => (
				<div>
					<FormControl variant="standard" sx={{ m: 1, minWidth: 120 }} className="row-select-drpdwn">
						<Select
							labelId="Reason for Delay"
							id="reasonForDelay"
							defaultOpen={true}
							value={p.row.reasonForDelay}
							onChange={(e: any) => {
								p.onRowChange({ ...p.row, reasonForDelay: e.target.value }, true);
							}}
							label="Reason for Delay"
						>
							<MenuItem value=""></MenuItem>
							{reasonList.map((reason) => (
								<MenuItem key={reason.id} value={reason.description}>
									{reason.description}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>
			)
		},
		{
			key: 'actionTaken',
			name: 'Action Taken',
			width: 120,
			minWidth: 120,
			editor: textEditor,
			cellClass: 'has-editor',
			formatter(props) {
				return props.row.actionTaken ? <OverflowTooltip>{props.row.actionTaken}</OverflowTooltip> : 'Action Taken?';
			}
		},
		{
			key: 'ticketNumber',
			name: 'Ticket No',
			width: 120,
			minWidth: 120,
			editor: textEditor,
			cellClass: 'has-editor',
			formatter(props) {
				return props.row.ticketNumber ? <OverflowTooltip>{props.row.ticketNumber}</OverflowTooltip> : 'Ticket No?';
			}
		},
		{
			key: 'downstream',
			name: 'Follow Up',
			width: 120,
			minWidth: 120,
			editor: textEditor,
			cellClass: 'has-editor',
			formatter(props) {
				return props.row.downstream ? <OverflowTooltip>{props.row.downstream}</OverflowTooltip> : 'Follow Up?';
			}
		},
		{
			key: 'userName',
			name: 'Last Updated By',
			minWidth: 150,
			width: 150
		},
		{
			key: 'updatedAt',
			name: 'Last Updated At',
			minWidth: 150,
			width: 150,
			formatter(props) {
				const updatedAt = props.row.updatedAt;
				if (updatedAt) {
					return moment(updatedAt).format();
				}
				return '';
			}
		},
		{
			key: 'schedulerName',
			name: 'Scheduler',
			width: 100,
			minWidth: 100
		},
		{
			key: 'application',
			name: 'Application',
			formatter(props) {
				return props.row.application || ' ';
			},
			minWidth: 115,
			width: 115
		},
		{
			key: 'subApplication',
			name: 'Sub Application',
			formatter(props) {
				return props.row.subApplication || ' ';
			},
			minWidth: 135,
			width: 135
		},
		{
			key: 'commonStatus',
			name: 'Common Status',
			minWidth: 155,
			width: 155,
			cellClass: 'center-text',
			formatter(props) {
				const status = props.row.commonStatus;
				return renderStatus(status);
			}
		},
		{
			key: 'machine',
			name: 'Machine',
			minWidth: 125,
			width: 125,
			formatter(props) {
				return props.row.machine;
			}
		},
		{
			key: 'jobType',
			name: 'Job Type',
			minWidth: 95
		},
		{
			key: 'actualDuration',
			name: 'Actual Duration',
			minWidth: 160,
			width: 150
		},
		{
			key: 'overRun',
			name: 'Over Run',
			minWidth: 95,
			formatter(props) {
				return props.row.overRun ? 'Yes' : 'No';
			}
		},
		{
			key: 'thresholdPercent',
			name: 'Threshold %',
			minWidth: 120,
			width: 150,
			cellClass: 'center-text',
			formatter(props) {
				const thresholdPercent = props.row.thresholdPercent;
				return renderThreshold(thresholdPercent);
			}
		},
		{
			key: 'avgRunTime',
			name: 'Avg Exec Duration',
			minWidth: 150,
			width: 180,
			formatter(props) {
				const avgRunTime = props.row.avgRunTime;
				return convertSeconds(avgRunTime);
			}
		},
		{
			key: 'durationDelay',
			name: 'Duration of Delay',
			minWidth: 150,
			width: 180
		}
	];
}

export default function JobExecutions() {

	function toggleSubRow(name: string, runStartTime: string, runEndTime: any) {
		const boxRows = localStorage.getItem('boxRows');
		const rows = boxRows ? JSON.parse(boxRows) : executionsList;
		const rowIndex = rows.findIndex((r: any) => r.name === name && r.runStartTime === runStartTime);
		const row = rows[rowIndex];
		const newRows = [...rows];
		let children: Row[] = [];
		let childRows: Row[] = [];
		if (row.isExpanded) {
			children = row.children;
			newRows.splice(Number(rowIndex) + 1, children.length);
			newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded };
			localStorage.setItem('boxRows', JSON.stringify(newRows));
			setExecutionsList(newRows);
		} else {
			const start = moment(runStartTime).startOf('day').subtract(1, 'hour');
			const end = moment(runStartTime).endOf('day').subtract(1, 'hour');
			const time = row.scheduleTime;
			const boxPromise = getBoxRunsList(name, start, end, time);
			setBoxLoading(true);
			boxPromise.then((res) => {
				setBoxLoading(false);
				if (res.success) {
					childRows = res.data;
				}
				children = childRows.map((x: any) => {
					return { ...x, isChildren: true };
				});
				newRows.splice(Number(rowIndex) + 1, 0, ...children);
				newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded, children };
				localStorage.setItem('boxRows', JSON.stringify(newRows));
				setExecutionsList(newRows);
			});
		}
	}

	const theme= useTheme();
	const colors = theme.palette;
	const [searchParams, setSearchParams] = useSearchParams();
	const hasDrillDowned = searchParams.has('isDrillDowned');
	const [executionsList, setExecutionsList] = useState<any[]>([]);
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [loading, setLoading] = useState(false);
	const [boxLoading, setBoxLoading] = useState(false);
	const minJobExecDate = '2023-03-01';
	const dateFrom: string = searchParams.get('fromDate')?.substring(0, 10) as string;
	const dateTo: string = searchParams.get('toDate')?.substring(0, 10) as string;
	const startDt = moment(dateFrom).startOf('day').subtract(1, 'hours').format('YYYY-MM-DD HH:mm');
	const endDt = moment(dateTo).endOf('day').subtract(1, 'hours').format('YYYY-MM-DD HH:mm');

	const getInitialFromDateTime = () => {
		if (hasDrillDowned && searchParams.has('fromDate')) {
			return startDt;
		}
		if (hasDrillDowned && searchParams.has('runStartTime')) {
			return moment.utc(searchParams.get('runStartTime')).tz('America/New_York');
		}
		return moment().startOf('day').subtract(1, 'hours');
	};

	const getInitialToDateTime = () => {
		if (hasDrillDowned && searchParams.has('toDate')) {
			return endDt;
		}
		if (hasDrillDowned && searchParams.has('runEndTime')) {
			return moment.utc(searchParams.get('runEndTime')).tz('America/New_York');
		}
		return moment().endOf('day').subtract(1, 'hours');
	};

	const [fromDateTime, setFromDateTime] = useState<any>(getInitialFromDateTime());
	const [toDateTime, setToDateTime] = useState<any>(getInitialToDateTime());
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });

	const [filterData, setFilterData] = useState<Row[]>([]);
	const [filterInfo, setFilterInfo] = useState([]);
	const [searchDetails, setSearchDetails] = useState([]);
	const [reasonList, setReasonList] = useState<any[]>([]);
	const dataFetchedRef = useRef(false);
	moment.updateLocale(moment.locale(), { invalidDate: '' });

	const renderStatus = (status: any) => {
		const formattedStatus = status?.toString().toUpperCase() || '--';
		const wideStyle = { minWidth: '125px', backgroundColor: '#299BFF26', color: '#299BFF' };

		switch (status) {
			case 'SUCCESS':
			case 'Successful':
				return <Chip variant="chipLabel" title={status} label={formattedStatus} style={{ backgroundColor: '#01C00833', color: '#01C008' }} />;
			case 'FAILURE':
			case 'Failure':
			case 'TERMINATED':
			case 'Terminated':
			case 'SUSPENDED':
				return <Chip variant="chipLabel" title={status} label={formattedStatus} style={{ backgroundColor: '#FFCCD7', color: '#FF0037' }} />;
			case 'NOT STARTED':
			case 'Not Started':
				return <Chip variant="chipLabel" title={status} label={formattedStatus} style={{ backgroundColor: '#1B1E35CC', color: '#71758F' }} />;
			case 'RUNNING':
			case 'ACTIVATED':
			case 'In Progress':
			case 'Hold':
				return <Chip variant="chipLabel" title={status} label={formattedStatus} style={{ backgroundColor: '#299BFF26', color: '#299BFF' }} />;
			case 'Wait Condition':
			case 'Wait Resource':
			case 'Wait Time Window':
				return <Chip variant="chipLabel" title={status} label={formattedStatus} style={wideStyle} />;
			default:
				return <Chip variant="chipLabel" title={status} label={formattedStatus} style={{ backgroundColor: '#299BFF26', color: '#299BFF' }} />;
		}
	};

	const renderThreshold = (threshold: any) => {
		let color = '#2DC8A8';
		if (threshold > 80 && threshold <= 100) color = '#FFC258';
		else if (threshold > 100) color = '#FE8083';
		return (
			<span color="success" style={{ color: color }}>
				{threshold} %
			</span>
		);
	};

	const columns = getColumns(toggleSubRow, renderStatus, renderThreshold, reasonList);

	const defaultColumnNames = [
		'Scheduled Job',
		'Box Name',
		'Sector',
		'System',
		'Process',
		'Run Status',
		'Start Time',
		'End Time',
		'SLA Status',
		'Normal Completion Time',
		'Estimated End Time',
		'Critical',
		'Schedule Time',
		'Schedule Date',
		'Reason',
		'Action Taken',
		'Ticket No',
		'Follow Up',
		'Last Updated By',
		'Last Updated At'
	];

	const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, value: number) => {
		setLoading(true);
		const promise1 = fetchJobExecutions(
			value,
			pageSize,
			sortingInfo,
			fromDateTime,
			toDateTime,
			parseFilter(filterInfo)
		);
		promise1.then(() => {
			setLoading(false);
		});
	}, [filterInfo, page, pageSize, fromDateTime, toDateTime]);

	const handlepageSizeChange = useCallback((event: any) => {
		setLoading(true);
		const promise1 = fetchJobExecutions(
			'1',
			event.target.value,
			sortingInfo,
			fromDateTime,
			toDateTime,
			parseFilter(filterInfo)
		);
		promise1.then(() => {
			setLoading(false);
		});
	}, [filterInfo, page, pageSize, fromDateTime, toDateTime]);

	const handleSorting = useCallback((sortingInfo: any) => {
		setLoading(true);
		const currentFilterInfo = parseFilter(filterInfo);
		const promise1 = fetchJobExecutions(page, pageSize, sortingInfo, fromDateTime, toDateTime, currentFilterInfo);
		promise1.then(() => {
			setLoading(false);
		});
	}, [filterInfo, page, pageSize, fromDateTime, toDateTime]);

	const resetSearchParams = () => {
		if (hasDrillDowned || searchParams.has('fromDate') || searchParams.has('toDate')) {
			searchParams.delete('status');
			searchParams.delete('scheduleDate');
			searchParams.delete('isDrillDowned');
			searchParams.delete('fromDate');
			searchParams.delete('toDate');
			setSearchParams(searchParams);
		}
	};

	const handleDateTimeChange = useCallback((field: any, dateTime: any) => {
		if (field === 'fromDateTime') setFromDateTime(dateTime);
		else setToDateTime(dateTime);
	}, []);
	const applyDateTimeRange = (e: any) => {
		resetSearchParams();
		setLoading(true);
		const promise1 = fetchJobExecutions(page, pageSize, sortingInfo, fromDateTime, toDateTime, parseFilter(filterInfo));
		setDateRangeMenuOpen(null);
		promise1.then(() => {
			setLoading(false);
		});
	};
	const handleFilter = useCallback((filterInfo: any) => {
		resetSearchParams();
		setFilterInfo(filterInfo);
		setLoading(true);
		const currentFromDateTime = fromDateTime;  
		const currentToDateTime = toDateTime;
	  
		const promise1 = fetchJobExecutions(1, pageSize, sortingInfo, currentFromDateTime, currentToDateTime, parseFilter(filterInfo));
		promise1.then(() => {
			// document.body.classList.remove('loading-indicator')
			setLoading(false);
		});
	  }, [fromDateTime, toDateTime, pageSize, sortingInfo]);

	const handleSearch = useCallback((searchDetails: any) => {
		setSearchDetails([]);
		jobSearchForExecutionsFunc(searchDetails, parseFilter(filterInfo, 'name'));
	}, [filterInfo, fromDateTime, toDateTime, pageSize, sortingInfo]);

	const parseFilter = (filterInfo: any, name?: string) => {
		// Call only filterable columns and reset the filtering in case of wrong/empty column
		const parsedFilter: any = {};
		Object.keys(filterInfo).forEach((key: any) => {
			if (key !== name && filterInfo[key]?.length) {
				parsedFilter[key] = filterInfo[key];
			}
		});
		return parsedFilter;
	};

	const buildFilterQuerySkeleton = (_status: string | null, _filter: string | null) => {
		const filterQuerySkeleton = {} as any;
		if (_status && hasDrillDowned) {
			switch (_status) {
				case 'SLA met':
					filterQuerySkeleton.slaStatus = ['Met'];
					filterQuerySkeleton.criticalFlag = ['true'];
					break;
				case 'SLA not met':
					filterQuerySkeleton.slaStatus = ['Not Met'];
					filterQuerySkeleton.criticalFlag = ['true'];
					break;
				case 'SLA not applicable':
					filterQuerySkeleton.slaStatus = ['NA'];
					filterQuerySkeleton.criticalFlag = ['true'];
					filterQuerySkeleton.commonStatus = ['Hold', 'In Progress', 'Terminated', 'Failure'];
					break;
				case 'SLA action taken':
					filterQuerySkeleton.slaStatus = ['Not Met'];
					filterQuerySkeleton.criticalFlag = ['true'];
					filterQuerySkeleton.slaActionTaken = ['Yes'];
					break;
				case 'Total Critical Executions':
					filterQuerySkeleton.criticalFlag = ['true'];
					break;
				case 'Successful':
					filterQuerySkeleton.commonStatus = ['Successful'];
					break;
				case 'Hold/ In Progress':
					filterQuerySkeleton.commonStatus = ['Hold', 'In Progress'];
					break;
				case 'Failed/ Terminated':
					filterQuerySkeleton.commonStatus = ['Failure', 'Terminated'];
					break;
				case 'Not Started':
					filterQuerySkeleton.commonStatus = ['Not Started'];
					break;
				case 'Not Started Critical':
					filterQuerySkeleton.commonStatus = ['Not Started'];
					filterQuerySkeleton.criticalFlag = ['true'];
					break;
				case 'Stable (Below 80% threshold)':
					filterQuerySkeleton.stable = ['Yes'];
					break;
				case 'Attention required(Est.duration exceeded)':
					filterQuerySkeleton.overRun = ['Yes'];
					break;
				case 'At risk (80% threshold exceeded)':
					filterQuerySkeleton.atRisk = ['Yes'];
					filterQuerySkeleton.criticalFlag = ['true'];
					break;
				case 'Exec. success':
					filterQuerySkeleton.commonStatus = ['Successful'];
					filterQuerySkeleton.criticalFlag = ['false'];
					break;
				case 'Exec. failed':
					filterQuerySkeleton.commonStatus = ['Failure', 'Terminated'];
					filterQuerySkeleton.criticalFlag = ['false'];
					break;
				case 'Not started':
					filterQuerySkeleton.commonStatus = ['Not Started'];
					filterQuerySkeleton.criticalFlag = ['false'];
					break;
				case 'Others':
					filterQuerySkeleton.commonStatus = ['Hold', 'In Progress'];
					filterQuerySkeleton.criticalFlag = ['false'];
					break;
				case 'At Risk':
					filterQuerySkeleton.atRisk = ['Yes'];
					filterQuerySkeleton.runStatus = ['SUCCESS'];
					break;
				case 'Action Taken':
					filterQuerySkeleton.criticalFlag = ['false'];
					filterQuerySkeleton.slaStatus = ['Not Met'];
					filterQuerySkeleton.actionTaken = ['NA'];
					break;
				case 'Exec Failed':
					filterQuerySkeleton.criticalFlag = ['true'];
					filterQuerySkeleton.commonStatus = ['Failure', 'Terminated'];
					break;
				case 'Non Critical Execution Failed':
					filterQuerySkeleton.criticalFlag = ['false'];
					filterQuerySkeleton.commonStatus = ['Failure', 'Terminated'];
					break;
				case 'SLAs Not Met':
					filterQuerySkeleton.slaStatus = ['Not Met'];
					break;
				case 'Reason Not Met':
					filterQuerySkeleton.criticalFlag = ['true'];
					filterQuerySkeleton.reasonForDelay = [`${_filter}`];
					break;
				default:
					break;
			}
		} else {
			columns.forEach((column: any) => {
				filterQuerySkeleton[column.key] = [];
			});
		}
		return filterQuerySkeleton;
	};

	const getStoredState = () => {
		let storedJobExecutionsState = localStorage.getItem('jobExecutionsState')
			? JSON.parse(localStorage.getItem('jobExecutionsState') ?? '')
			: null;
		if (localStorage.getItem('selectedAlert')) {
			storedJobExecutionsState = localStorage.getItem('alertJobExecutionsState')
				? JSON.parse(localStorage.getItem('alertJobExecutionsState') ?? '')
				: null;
			localStorage.removeItem('alertJobExecutionsState');
		}
		return storedJobExecutionsState;
	};

	const fetchSlaActionTaken = (filterQuerySkeleton: any) => {
		const drillDownPromise = fetchActionTakenData();
		localStorage.setItem('drill-down-execution', 'drill-true');
		return drillDownPromise.then((value) => {
			filterQuerySkeleton.actionTaken = value;
			return fetchJobExecutions(
				page,
				pageSize,
				sortingInfo,
				fromDateTime,
				toDateTime,
				parseFilter(filterQuerySkeleton)
			);
		});
	};

	const fetchDrillDown = (filterQuerySkeleton: any, _slaStatus: any, _name: any, _runStartTime: any, _runEndTime: any) => {
		if (_slaStatus) {
			filterQuerySkeleton.slaStatus = ['Not Met'];
		}
		if (_name) {
			filterQuerySkeleton.name = [_name];
		}
		if (_runStartTime && _runEndTime) {
			filterQuerySkeleton.runStartTime = [moment(_runStartTime).format('YYYY-MM-DD HH:mm')];
			filterQuerySkeleton.runEndTime = [moment(_runEndTime).format('YYYY-MM-DD HH:mm')];
		}
		localStorage.setItem('drill-down-execution', 'drill-true');
		return fetchJobExecutions(
			page,
			pageSize,
			sortingInfo,
			fromDateTime,
			toDateTime,
			parseFilter(filterQuerySkeleton)
		);
	};

	const fetchFromStoredState = (storedJobExecutionsState: any) => {
		const isDrillDownCondition = localStorage.getItem('drill-down-execution');
		if (isDrillDownCondition === 'drill-true') {
			localStorage.setItem('drill-down-execution', 'false');
		} else {
			localStorage.setItem('drill-down-execution', 'true');
		}
		const promise1 = fetchJobExecutions(
			1,
			storedJobExecutionsState.pageSize,
			storedJobExecutionsState.sortingInfo,
			storedJobExecutionsState.fromDate
				? moment(storedJobExecutionsState.fromDate)
				: fromDateTime,
			storedJobExecutionsState.toDate
				? moment(storedJobExecutionsState.toDate)
				: toDateTime,
			parseFilter(storedJobExecutionsState.filterInfo)
		);
		setPaginationData(storedJobExecutionsState.page);
		setFilterData(storedJobExecutionsState.filterOptions);
		setPage(1);
		setPageSize(storedJobExecutionsState.pageSize);
		setSortingInfo(storedJobExecutionsState.sortingInfo);
		setFilterInfo(storedJobExecutionsState.filterInfo);
		return promise1;
	};

	const fetchDefault = (filterQuerySkeleton: any) => {
		const promise1 = fetchJobExecutions(
			page,
			pageSize,
			sortingInfo,
			fromDateTime,
			toDateTime,
			parseFilter(filterQuerySkeleton)
		);
		const isDrillDownCondition = localStorage.getItem('drill-down-execution');
		if (isDrillDownCondition === 'drill-true') {
			localStorage.setItem('drill-down-execution', 'false');
		} else {
			localStorage.setItem('drill-down-execution', 'true');
		}
		return promise1;
	};

	const executeInitialFetch = (
		_status: string | null,
		_slaStatus: string | null,
		_name: string | null,
		_runStartTime: string | null,
		_runEndTime: string | null,
		filterQuerySkeleton: any,
		storedJobExecutionsState: any
	) => {
		if (hasDrillDowned && _status === 'SLA action taken') {
			return fetchSlaActionTaken(filterQuerySkeleton);
		}
		if (hasDrillDowned) {
			return fetchDrillDown(filterQuerySkeleton, _slaStatus, _name, _runStartTime, _runEndTime);
		}
		if (storedJobExecutionsState?.pageNumber && storedJobExecutionsState?.pageSize) {
			return fetchFromStoredState(storedJobExecutionsState);
		}
		return fetchDefault(filterQuerySkeleton);
	};

	const updateActiveIdClasses = () => {
		const url = globalThis.location.pathname;
		const activeId = document.getElementById('root');
		const jobName = url.substring(url.lastIndexOf('/') + 1);
		if (activeId) {
			if (globalThis.location.pathname === '/jobExecutions') {
				activeId.classList.add('jobs-list-table');
				activeId.classList.remove('jobs-list', 'root');
				document.body.classList.add('jobs-exe-datas');
				document.body.classList.remove('jobs-run-table');
				document.body.classList.add('jobs-list-datas', 'datefilter-align');
			} else if (jobName !== globalThis.location.pathname && globalThis.location.pathname === '/jobExecutions/' + jobName) {
				activeId.classList.add('jobs-list');
				document.body.classList.remove('jobs-list-datas');
				activeId.classList.remove('jobs-list-table', 'root');
				document.body.classList.remove('jobs-run-table', 'jobs-exe-datas');
			} else {
				document.body.classList.remove('jobs-list-datas');
				activeId.classList.add('root');
				activeId.classList.remove('jobs-list-table', 'jobs-list');
				document.body.classList.remove('jobs-run-table', 'jobs-exe-datas');
			}
		}
	};

	useEffect(() => {
		if (dataFetchedRef.current) return;
		dataFetchedRef.current = true;
		setLoading(true);

		const _status = searchParams.get('status');
		const _filter = searchParams.get('filterReason');
		const _slaStatus = searchParams.get('slaStatus');
		const _name = searchParams.get('name');
		const _runStartTime = searchParams.get('runStartTime');
		const _runEndTime = searchParams.get('runEndTime');

		const filterQuerySkeleton = buildFilterQuerySkeleton(_status, _filter);
		const storedState = getStoredState();

		const promise1 = executeInitialFetch(
			_status,
			_slaStatus,
			_name,
			_runStartTime,
			_runEndTime,
			filterQuerySkeleton,
			storedState
		);

		promise1.then(() => {
			setLoading(false);
		});

		updateActiveIdClasses();
	}, []);

	async function jobSearchForExecutionsFunc(searchDetails: any, jobFilterInfo: any) {
		const res = await jobSearchForExecutions(searchDetails, jobFilterInfo, fromDateTime, toDateTime);
		if (res.success) {
			setSearchDetails(res.data);
		}
	}

	const handleEditCell = useCallback((rows: any[], editData: any) => {
		const runId = rows[editData.indexes[0]].id;
		const key = editData.column.key;
		const patchData: any = {};
		patchData[key] = rows[editData.indexes[0]][key];
		patchData.runId = runId;
		patchJobSchedule(patchData, rows);
	}, []);

	async function fetchJobExecutions(
		pageNumber: any,
		size: any,
		sortingInfo: any,
		fromDateTime: any,
		toDateTime: any,
		jobFilterInfo: any
	) {
		setLoading(true);
		setFromDateTime(fromDateTime);
		setToDateTime(toDateTime);
		const res = await getJobExecutions(
			pageNumber.toString(),
			size.toString(),
			sortingInfo,
			jobFilterInfo,
			fromDateTime,
			toDateTime,
			false
		);
		if (res.success) {
			const reasonsRes = await getReasons('1', '500', '');
			setReasonList(reasonsRes.data.slaNotMetReasons);
			setExecutionsList(res.data.jobs);
			setPaginationData(res.data.page);
			setFilterData(res.data.filterOptions);
			setPage(pageNumber);
			setPageSize(size);
			setSortingInfo(sortingInfo);
			setFilterInfo(jobFilterInfo);
			setLoading(false);
			const jobExecutionsState = {
				page: res.data.page,
				filterOptions: res.data.filterOptions,
				fromDate: fromDateTime,
				toDate: toDateTime,
				pageSize: size,
				pageNumber,
				sortingInfo,
				filterInfo: jobFilterInfo
			};
			localStorage.setItem('jobExecutionsState', JSON.stringify(jobExecutionsState));
			localStorage.setItem('boxRows', JSON.stringify(res.data.jobs));
		}
		setLoading(false);
	}

	async function fetchActionTakenData() {
		setLoading(true);
		const res = await getActionTakenData(fromDateTime, toDateTime);
		if (res.success) {
			return res.data;
		}
		setLoading(false);
	}

	async function patchJobSchedule(patchData: any, rows: Row[]) {
		setLoading(true);
		const res = await updateJobRunHistory(patchData);
		if (res.success) {
			setLoading(false);
			setExecutionsList(rows);
		}
	}
	// [Date Range Menu]
	const [dateRangeMenuOpen, setDateRangeMenuOpen] = React.useState<null | HTMLElement>(null);

	const handleDateClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setDateRangeMenuOpen(event.currentTarget);
	}, []);

	const handleDateRangeMenu = useCallback( () => {
		setDateRangeMenuOpen(null);
	}, []);

	const open = Boolean(dateRangeMenuOpen);

	return (
		<div className="job-schedules-wrap">
			<div className="job-exexutions-dateFilter-div">
				{/* [Date Range Menu] */}
				<div style={{ backgroundColor: colors.backgroundLight.dark, display: 'flex', alignItems: 'center', padding: '5px 0px 5px 0px', marginTop: '5px', marginBottom: '1px' }}>
					{/* condition for default Today  */}
					{moment(fromDateTime).format('DD MM YYYY, HH:mm') ===
						moment().startOf('day').subtract(1, 'hours').format('DD MM YYYY, HH:mm') &&
					moment(toDateTime).format('DD MM YYYY, HH:mm') ===
						moment().endOf('day').subtract(1, 'hours').format('DD MM YYYY, HH:mm') ? (
						<Typography variant='filterText' sx={{ marginRight: '80px', backgroundColor: colors.backgroundLight.dark }}>Today</Typography>
					) : (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								marginRight: '5px',
								backgroundColor: colors.backgroundLight.dark
							}}
						>
							<Typography variant="filterText">{fromDateTime && moment(fromDateTime).tz('America/New_York').format('DD-MM-YYYY hh:mm')}</Typography>
							<Typography variant="filterText" style={{ marginLeft: '4px', marginRight: '4px' }}>to</Typography>
							<Typography variant="filterText">{toDateTime && moment(toDateTime).tz('America/New_York').format('DD-MM-YYYY hh:mm')}</Typography>
						</div>
					)}

					<button
						onClick={handleDateClick}
						style={{
							cursor: 'pointer',
							background: 'none',
							border: 'none',
							padding: 0
						}}
					>
						<DatePickerIcon />
					</button>
				</div>

				<Menu
					id="date-range-menu"
					MenuListProps={{
						'aria-labelledby': 'date-range-button',
						sx:{
							padding: '0px !important'
						}
					}}
					anchorEl={dateRangeMenuOpen}
					open={open}
					onClose={handleDateRangeMenu}
					slotProps={{
						paper: {
							style: {
								width: '30ch'
							},
							sx: {
								backgroundColor: `${colors.dropdown.menuItemBackground} !important`,
							},
						},
					}}
				>
					{/* From date */}
					<Stack spacing={2} sx={{ p: 2, backgroundColor: `${colors.dropdown.menuItemBackground} !important`, borderRadius: 1 }}>
						{/* Title */}
						<Typography
							variant="bodyText"
							sx={{ color: `${colors.dropdown.menuItemText} !important`, fontWeight: 600 }}
						>
							Absolute time range
						</Typography>

						{/* From date */}
						<Stack spacing={1}>
							<Typography variant="bodyText" sx={{ color: `${colors.dropdown.menuItemText} !important` }}>
							From
							</Typography>
							<LocalizationProvider dateAdapter={AdapterMoment}>
							<DateTimePicker
								showToolbar={true}
								components={{
								OpenPickerIcon: DatePickerIcon,
								}}
								PopperProps={{ 
									placement: "auto",
									sx: {
										"& .MuiPaper-root": {
											backgroundColor: `${colors.dropdown.background} !important`,
										},
										"& .MuiPickersDay-root": {
											color: `${colors.dropdown.menuItemHoverText} !important`,
											"&.Mui-selected": {
											backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
											},
											"&:hover": {
											backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
											},
										},
										"& .MuiTypography-root": {
											color: `${colors.dropdown.menuItemHoverText} !important`,
										},
									},
								 }}
								toolbarTitle={
								<Stack direction="row" spacing={1}>
									<Typography sx={{ color: `${colors.dropdown.menuItemText} !important` }}>
									Select Date Time
									</Typography>
									<Typography
									variant="caption"
									sx={{ color: `${colors.dropdown.menuItemText} !important`, opacity: 0.8 }}
									>
									{`Timezone: ${moment().isDST() ? "EDT(UTC-4)" : "EST(UTC-5)"}`}
									</Typography>
								</Stack>
								}
								ampm={false}
								value={moment(fromDateTime).tz("America/New_York")}
								minDateTime={moment(minJobExecDate)
								.tz("America/New_York")
								.startOf("day")
								.subtract(1, "hour")}
								maxDateTime={moment()
								.tz("America/New_York")
								.endOf("day")
								.subtract(1, "hours")}
								onChange={(dateTime) => {
								handleDateTimeChange("fromDateTime", dateTime);
								}}
								renderInput={(params: any) => (
								<TextField
									{...params}
									name="startDateTime"
									sx={{
										"& .MuiInputBase-root": {
											...theme.typography.filterText,
											backgroundColor: `${colors.dropdown.background} !important`,
											color: `${colors.dropdown.menuItemHoverText} !important`,
										},
										"& .MuiInputBase-input": {
											color: `${colors.dropdown.menuItemHoverText} !important`,
										},
									}}
								/>
								)}
							/>
							</LocalizationProvider>
						</Stack>

						{/* To date */}
						<Stack spacing={1}>
							<Typography variant="bodyText" sx={{ color: `${colors.dropdown.menuItemText} !important` }}>
							To
							</Typography>
							<LocalizationProvider dateAdapter={AdapterMoment}>
							<DateTimePicker
								showToolbar={true}
								components={{
								OpenPickerIcon: DatePickerIcon,
								}}
								PopperProps={{ 
									placement: "auto",
									sx: {
										"& .MuiPaper-root": {
											backgroundColor: `${colors.dropdown.background} !important`,
										},
										"& .MuiPickersDay-root": {
											color: `${colors.dropdown.menuItemHoverText} !important`,
											"&.Mui-selected": {
											backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
											},
											"&:hover": {
											backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
											},
										},
										"& .MuiTypography-root": {
											color: `${colors.dropdown.menuItemHoverText} !important`,
										},
									},
								}}
								toolbarTitle={
								<Stack direction="row" spacing={1}>
									<Typography sx={{ color: `${colors.dropdown.menuItemText} !important` }}>
									Select Date Time
									</Typography>
									<Typography
									variant="caption"
									sx={{ color: `${colors.dropdown.menuItemText} !important`, opacity: 0.8 }}
									>
									{`Timezone: ${moment().isDST() ? "EDT(UTC-4)" : "EST(UTC-5)"}`}
									</Typography>
								</Stack>
								}
								ampm={false}
								value={moment(toDateTime).tz("America/New_York")}
								minDateTime={moment(fromDateTime).tz("America/New_York")}
								maxDateTime={moment()
								.tz("America/New_York")
								.endOf("day")
								.subtract(1, "hour")}
								onChange={(dateTime) => {
								handleDateTimeChange("toDateTime", dateTime);
								}}
								renderInput={(params: any) => (
								<TextField
									{...params}
									name="endDateTime"
									sx={{
									"& .MuiInputBase-root": {
										...theme.typography.filterText,
										backgroundColor: `${colors.dropdown.background} !important`,
										color: `${colors.dropdown.menuItemHoverText} !important`,
									},
									"& .MuiInputBase-input": {
										color: `${colors.dropdown.menuItemHoverText} !important`,
									},
									}}
								/>
								)}
							/>
							</LocalizationProvider>
						</Stack>

						{/* Buttons */}
						<div className="date_range_button">
						<Button
							variant="contained"
							sx={{
								color: `${colors.dropdown.menuItemText} !important`,
							}}
							style={{ width: '50%' }}
							onClick={() => {
								setFromDateTime(moment().startOf('day').subtract(1, 'hours'));
								setToDateTime(moment().endOf('day').subtract(1, 'hours'));
								setDateRangeMenuOpen(null);
							}}
						>
							Clear
						</Button>

						<Button variant="contained" onClick={applyDateTimeRange} sx={{
							width: '50%',
							backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
							color: `${colors.dropdown.menuItemHoverText} !important`,
							"&:hover": {
							backgroundColor: `${colors.dropdown.menuItemHover} !important`,
						},
						}}>
							Apply
						</Button>
						</div>
					</Stack>
				</Menu>

				{/* <LocalizationProvider className="outerDateContainer" dateAdapter={AdapterMoment}>
					<FormControl className="dateFilter-formControl" sx={{ m: 1 }}>
						<DateTimePicker
							label="From Date"
							showToolbar={true}
							components={{
								OpenPickerIcon: DatePickerIcon
							}}
							PopperProps={{ placement: 'auto' }}
							toolbarTitle={
								<>
									Select Date Time{' '}
									<div className="time-picker-zone">{`Timezone: ${
										moment().isDST() ? 'EDT(UTC-4)' : 'EST(UTC-5)'
									}`}</div>
								</>
							}
							className="dateFilter"
							ampm={false}
							value={moment(fromDateTime).tz('America/New_York')}
							minDateTime={moment(minJobExecDate).tz('America/New_York').startOf('day').subtract(1, 'hour')}
							maxDateTime={moment().tz('America/New_York').endOf('day').subtract(1, 'hours')}
							onChange={(dateTime) => {
								handleDateTimeChange('fromDateTime', dateTime);
							}}
							renderInput={(params: any) => <TextField name="startDateTime" {...params} />}
						/>
					</FormControl>
					<FormControl className="dateFilter-formControl" style={{ marginBottom: '0px' }} sx={{ m: 1 }}>
						<DateTimePicker
							label="To Date"
							showToolbar={true}
							components={{
								OpenPickerIcon: DatePickerIcon
							}}
							PopperProps={{
								placement: 'auto'
							}}
							toolbarTitle={
								<>
									Select Date Time{' '}
									<div className="time-picker-zone">{`Timezone: ${
										moment().isDST() ? 'EDT(UTC-4)' : 'EST(UTC-5)'
									}`}</div>
								</>
							}
							className="dateFilter"
							ampm={false}
							value={moment(toDateTime).tz('America/New_York')}
							minDateTime={moment(fromDateTime).tz('America/New_York')}
							maxDateTime={moment().tz('America/New_York').endOf('day').subtract(1, 'hour')}
							onChange={(dateTime) => {
								handleDateTimeChange('toDateTime', dateTime);
							}}
							renderInput={(params: any) => <TextField name="endDateTime" {...params} />}
						/>
					</FormControl>
				</LocalizationProvider>
				<Button variant="contained" color="primary" className="dateRangeApplyBtn" onClick={applyDateTimeRange}>
					Apply
				</Button> */}
			</div>
			<Box>
				{/* <Grid container className={classes.metricContainer} spacing={2}>
					{!metricLoading &&
						scheduleMetrices.map((metric) => (
							<Grid item xs={2} key={metric.name} className="job-widgets">
								<Paper elevation={2} className={classes.paperForMetric}>
									<Box sx={{ marginBottom: '10px', marginLeft: '10px' }}>
										{metric.name}
										<Stack direction="row" alignItems="flextStart" spacing={15} sx={{ marginBottom: '10px' }}>
											<p className={metric.className}>{metric.value}</p>
											<span className={classes.metricImageSpan}>
												{/* <img src={metric.imgSrc} className={classes.metricImgSrc}></img> */}
				{/* {metric.arrowSrc ? (
													<>
														<img src={metric.arrowSrc} className={classes.metricArrowImg} />
														<b>{Math.abs(metric.percent)}%</b>
													</>
												) : (
													''
												)}
											</span>
										</Stack>
									</Box>
								</Paper>
							</Grid>
						))}
				</Grid> */}
				<div className="job-schedule-table">
					<Grid container item xs={12}>
						<Grid item xs={5}></Grid>
						<Grid item xs={4} />
					</Grid>
					<Grid>
						{loading ? (
							<Loader />
							
						) : (
							<CustomizedDataGrid
								tableColumns={columns}
								tableData={executionsList}
								tableType="Executions"
								defaultColumnNames={defaultColumnNames}
								onSorting={handleSorting}
								currSorting={sortingInfo}
								filterDataOptions={filterData}
								onFilterCheck={handleFilter}
								currFilter={filterInfo}
								showExport={true}
								fileName={`Job Executions-${moment(fromDateTime).format()}.xlsx`}
								searchDataValues={searchDetails}
								onFilterSearch={handleSearch}
								onCellEdit={handleEditCell}
								page={page}
								pageSize={pageSize}
								paginationData={paginationData}
								listLength={executionsList.length}
								handlepageSizeChange={handlepageSizeChange}
								handlePageChange={handlePageChange}
								isDateRangeFilter={true}
								isDisabled={false}
								isLighterTheme={true}
							/>
						)}
						{boxLoading ? <Loader /> : ''}
					</Grid>
				</div>
			</Box>
		</div>
	);
}
